import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addFieldSalesRepresentative,
  fetchFieldSalesRepresentatives,
  fetchFieldSalesRepresentativesAsCustomer,
  removeFieldSalesRepresentative,
  type CreateFieldSalesRepresentativeRequest,
} from '../api';

export const fieldSalesRepresentativeKeys = {
  all: ['fieldSalesRepresentatives'] as const,
  asCustomer: (customerCompanyId: string) =>
    [
      ...fieldSalesRepresentativeKeys.all,
      'as-customer',
      customerCompanyId,
    ] as const,
  assignment: (companyId: string, customerCompanyId: string) =>
    [
      ...fieldSalesRepresentativeKeys.all,
      'assignment',
      companyId,
      customerCompanyId,
    ] as const,
};

export function useFieldSalesRepresentativesAsCustomer(
  customerCompanyId: string,
  enabled = true
) {
  return useQuery({
    queryKey: fieldSalesRepresentativeKeys.asCustomer(customerCompanyId),
    queryFn: () => fetchFieldSalesRepresentativesAsCustomer(customerCompanyId),
    enabled: enabled && !!customerCompanyId,
  });
}

export function useFieldSalesRepresentatives(
  companyId: string,
  customerCompanyId: string,
  enabled = true
) {
  return useQuery({
    queryKey: fieldSalesRepresentativeKeys.assignment(
      companyId,
      customerCompanyId
    ),
    queryFn: () => fetchFieldSalesRepresentatives(companyId, customerCompanyId),
    enabled: enabled && !!companyId && !!customerCompanyId,
  });
}

export function useAddFieldSalesRepresentative(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFieldSalesRepresentativeRequest) =>
      addFieldSalesRepresentative(companyId, request),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: fieldSalesRepresentativeKeys.assignment(
          companyId,
          request.customerCompanyId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: fieldSalesRepresentativeKeys.asCustomer(
          request.customerCompanyId
        ),
      });
    },
  });
}

export function useRemoveFieldSalesRepresentative(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerCompanyId: string) =>
      removeFieldSalesRepresentative(companyId, customerCompanyId),
    onSuccess: (_, customerCompanyId) => {
      queryClient.invalidateQueries({
        queryKey: fieldSalesRepresentativeKeys.assignment(
          companyId,
          customerCompanyId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: fieldSalesRepresentativeKeys.asCustomer(customerCompanyId),
      });
    },
  });
}
